import { CalendarRange, Download, FileText, Truck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { SummaryCard } from "../components/common/SummaryCard";
import { TablePagination } from "../components/common/TablePagination";
import { createResource, extractErrorMessage, fetchSettings } from "../lib/api";
import { downloadPdfUrl } from "../lib/pdfActions";

const DEFAULT_PAGE_SIZE = 30;
const REPORT_TYPES = [
  {
    value: "revenue-summary",
    label: "Revenue Summary",
    description: "Revenue-first view of invoiced loads in the selected date range.",
  },
  {
    value: "miles-summary",
    label: "Miles Summary",
    description: "Mileage-focused load report with totals and breakdowns.",
  },
  {
    value: "truck-wise",
    label: "Truck-wise Report",
    description: "Grouped by truck with revenue, mileage, drivers, states, and countries.",
  },
];
const REPORT_FIELDS = [
  { key: "customer_name", label: "Customer" },
  { key: "rate", label: "Amount" },
  { key: "truck_number", label: "Truck" },
  { key: "driver_name", label: "Driver" },
  { key: "state_label", label: "State" },
  { key: "country_label", label: "Country" },
  { key: "distance_miles", label: "Miles" },
];
const DEFAULT_FIELDS = ["customer_name", "rate", "truck_number", "driver_name", "state_label", "country_label"];
const BREAKDOWN_OPTIONS = [
  { key: "include_customer_breakdown", label: "Customer grouping" },
  { key: "include_driver_breakdown", label: "Driver grouping" },
  { key: "include_truck_breakdown", label: "Truck grouping" },
];

const toDateInputValue = (value) => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, "0");
  const day = `${value.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildDefaultDateRange = () => {
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    start_date: toDateInputValue(startOfMonth),
    end_date: toDateInputValue(today),
  };
};

const buildDefaultForm = () => ({
  report_type: "revenue-summary",
  ...buildDefaultDateRange(),
  selected_fields: DEFAULT_FIELDS,
  include_customer_breakdown: true,
  include_driver_breakdown: true,
  include_truck_breakdown: true,
});

const formatMoney = (value, currency) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency, maximumFractionDigits: 2 }).format(value || 0);

const formatDistance = (miles, unit = "Miles") => {
  const displayValue = unit === "Kilometers" ? (miles || 0) * 1.60934 : miles || 0;
  return `${displayValue.toLocaleString("en-CA", { maximumFractionDigits: 1, minimumFractionDigits: 1 })} ${unit === "Kilometers" ? "km" : "mi"}`;
};

const formatDate = (value) => (value ? new Date(`${value}T00:00:00`).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" }) : "—");

const formatFieldValue = (fieldKey, row, distanceUnit) => {
  if (fieldKey === "rate") {
    return row.rate_display || formatMoney(row.rate, row.currency || "CAD");
  }
  if (fieldKey === "distance_miles") {
    return formatDistance(row.distance_miles, distanceUnit);
  }
  return row[fieldKey] || "—";
};

const BreakdownTable = ({ rows, title, testIdPrefix, distanceUnit }) => (
  <section className="table-shell" data-testid={`${testIdPrefix}-section`}>
    <div className="border-b border-zinc-200 px-5 py-4">
      <h3 className="text-xl font-semibold text-zinc-900" data-testid={`${testIdPrefix}-title`}>{title}</h3>
    </div>
    <div className="overflow-x-auto">
      <table className="data-table" data-testid={`${testIdPrefix}-table`}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Loads</th>
            <th>Trips</th>
            <th>{distanceUnit === "Kilometers" ? "Kilometers" : "Miles"}</th>
            <th>CAD</th>
            <th>USD</th>
          </tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row) => (
            <tr data-testid={`${testIdPrefix}-row-${row.name}`} key={`${testIdPrefix}-${row.name}`}>
              <td data-testid={`${testIdPrefix}-name-${row.name}`}>{row.name}</td>
              <td data-testid={`${testIdPrefix}-loads-${row.name}`}>{row.load_count}</td>
              <td data-testid={`${testIdPrefix}-trips-${row.name}`}>{row.trip_count}</td>
              <td data-testid={`${testIdPrefix}-distance-${row.name}`}>{formatDistance(row.distance_miles, distanceUnit)}</td>
              <td data-testid={`${testIdPrefix}-cad-${row.name}`}>{formatMoney(row.cad_total, "CAD")}</td>
              <td data-testid={`${testIdPrefix}-usd-${row.name}`}>{formatMoney(row.usd_total, "USD")}</td>
            </tr>
          )) : (
            <tr>
              <td className="py-10 text-center text-sm text-zinc-500" colSpan={6} data-testid={`${testIdPrefix}-empty`}>
                No grouped rows available.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </section>
);

export const ReportsPage = () => {
  const [settings, setSettings] = useState({ distance_unit: "Miles" });
  const [form, setForm] = useState(buildDefaultForm());
  const [pageLoading, setPageLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    const bootstrap = async () => {
      setPageLoading(true);
      try {
        const settingsPayload = await fetchSettings();
        setSettings({ distance_unit: settingsPayload.distance_unit || "Miles" });
      } catch (error) {
        toast.error(extractErrorMessage(error, "Unable to load report settings."));
      } finally {
        setPageLoading(false);
      }
    };
    bootstrap();
  }, []);

  const paginatedRows = useMemo(() => {
    if (!reportData?.rows?.length) {
      return [];
    }
    return reportData.rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  }, [currentPage, pageSize, reportData]);

  const reportTypeMeta = REPORT_TYPES.find((item) => item.value === form.report_type);

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const toggleField = (fieldKey) => {
    setForm((current) => ({
      ...current,
      selected_fields: current.selected_fields.includes(fieldKey)
        ? current.selected_fields.filter((item) => item !== fieldKey)
        : [...current.selected_fields, fieldKey],
    }));
  };

  const toggleBreakdown = (breakdownKey) => {
    setForm((current) => ({ ...current, [breakdownKey]: !current[breakdownKey] }));
  };

  const clearGeneratedReport = () => {
    setForm(buildDefaultForm());
    setReportData(null);
    setCurrentPage(1);
  };

  const validateForm = () => {
    if (!form.start_date || !form.end_date) {
      toast.error("Select both a start date and an end date before generating a report.");
      return false;
    }
    if (form.end_date < form.start_date) {
      toast.error("End date cannot be earlier than start date.");
      return false;
    }
    if (!form.selected_fields.length) {
      toast.error("Select at least one field for the report.");
      return false;
    }
    return true;
  };

  const handleGenerate = async () => {
    if (!validateForm()) {
      return;
    }
    setGenerating(true);
    try {
      const payload = await createResource("/reports/custom/preview", form);
      setReportData(payload);
      setCurrentPage(1);
      toast.success(`${payload.report_label} generated.`);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to generate the selected report."));
    } finally {
      setGenerating(false);
    }
  };

  const handleExportPdf = async () => {
    if (!reportData) {
      toast.error("Generate a report first, then download the PDF.");
      return;
    }
    try {
      setExporting(true);
      const payload = await createResource("/reports/custom/export", { ...form, export_format: "pdf" });
      await downloadPdfUrl(payload.file_url, payload.file_name || `${reportData.report_type}.pdf`);
      toast.success(`${payload.report_label} PDF downloaded.`);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to download the report PDF."));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="reports-page">
      <section className="space-y-3" data-testid="reports-page-header">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Reports</p>
        <h1 className="text-4xl font-semibold text-zinc-900" data-testid="reports-page-title">Build custom reports on demand</h1>
        <p className="max-w-3xl text-sm text-zinc-500" data-testid="reports-page-description">
          Choose a report type, pick the fields you need, review it on screen, and download the same report as PDF.
        </p>
      </section>

      <section className="panel-surface overflow-hidden px-5 py-5" data-testid="reports-builder-panel">
        <div className="grid gap-6 xl:grid-cols-[1.2fr_0.95fr]">
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <div className="rounded-3xl bg-zinc-900 p-3 text-white" data-testid="reports-builder-icon">
                <CalendarRange className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-zinc-900" data-testid="reports-builder-title">Choose report type</h2>
                <p className="mt-1 text-sm text-zinc-500" data-testid="reports-builder-subtitle">Revenue Summary, Miles Summary, and Truck-wise are ready now.</p>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3" data-testid="reports-report-type-cards">
              {REPORT_TYPES.map((reportType) => {
                const isActive = form.report_type === reportType.value;
                return (
                  <button
                    className={`rounded-[28px] border px-5 py-5 text-left transition-colors ${isActive ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-900 hover:border-zinc-400"}`}
                    data-testid={`reports-report-type-card-${reportType.value}`}
                    key={reportType.value}
                    onClick={() => setForm((current) => ({ ...current, report_type: reportType.value }))}
                    type="button"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-base font-semibold">{reportType.label}</span>
                      <Truck className="h-4 w-4 opacity-70" />
                    </div>
                    <p className={`mt-3 text-sm ${isActive ? "text-zinc-200" : "text-zinc-500"}`} data-testid={`reports-report-type-description-${reportType.value}`}>
                      {reportType.description}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="rounded-[32px] border border-zinc-200 bg-zinc-50/70 p-5" data-testid="reports-field-selection-panel">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-zinc-900" data-testid="reports-field-selection-title">Choose report fields</h3>
                  <p className="mt-1 text-sm text-zinc-500" data-testid="reports-field-selection-description">These are the load details available in the report table and PDF.</p>
                </div>
                <FileText className="h-5 w-5 text-zinc-500" />
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="reports-field-selection-grid">
                {REPORT_FIELDS.map((field) => {
                  const checked = form.selected_fields.includes(field.key);
                  return (
                    <button
                      className={`rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${checked ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"}`}
                      data-testid={`reports-field-toggle-${field.key}`}
                      key={field.key}
                      onClick={() => toggleField(field.key)}
                      type="button"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span>{field.label}</span>
                        <span data-testid={`reports-field-toggle-state-${field.key}`}>{checked ? "On" : "Off"}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rounded-[32px] border border-zinc-200 bg-white p-5" data-testid="reports-breakdown-panel">
              <h3 className="text-lg font-semibold text-zinc-900" data-testid="reports-breakdown-title">Grouping and totals</h3>
              <p className="mt-1 text-sm text-zinc-500" data-testid="reports-breakdown-description">Bottom totals stay visible, and grouped breakdowns can be shown below the table.</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="reports-breakdown-grid">
                {BREAKDOWN_OPTIONS.map((option) => {
                  const checked = Boolean(form[option.key]);
                  const disabled = option.key === "include_truck_breakdown" && form.report_type === "truck-wise";
                  return (
                    <button
                      className={`rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition ${checked ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-400"} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
                      data-testid={`reports-breakdown-toggle-${option.key}`}
                      disabled={disabled}
                      key={option.key}
                      onClick={() => toggleBreakdown(option.key)}
                      type="button"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span>{option.label}</span>
                        <span data-testid={`reports-breakdown-toggle-state-${option.key}`}>{checked ? "On" : "Off"}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="rounded-[32px] border border-zinc-200 bg-zinc-50/80 p-5" data-testid="reports-builder-form">
            <div className="space-y-4">
              <label className="block space-y-2" data-testid="reports-form-report-type-wrap">
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Report type</span>
                <select className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900" data-testid="reports-form-report-type-select" name="report_type" onChange={updateForm} value={form.report_type}>
                  {REPORT_TYPES.map((reportType) => <option key={reportType.value} value={reportType.value}>{reportType.label}</option>)}
                </select>
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block space-y-2" data-testid="reports-form-start-date-wrap">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Start date</span>
                  <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900" data-testid="reports-form-start-date-input" name="start_date" onChange={updateForm} type="date" value={form.start_date} />
                </label>
                <label className="block space-y-2" data-testid="reports-form-end-date-wrap">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">End date</span>
                  <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-900" data-testid="reports-form-end-date-input" name="end_date" onChange={updateForm} type="date" value={form.end_date} />
                </label>
              </div>

              <div className="rounded-[24px] border border-dashed border-zinc-300 bg-white px-4 py-4" data-testid="reports-form-selected-summary">
                <div className="text-sm font-semibold text-zinc-900" data-testid="reports-form-selected-report-label">{reportTypeMeta?.label}</div>
                <div className="mt-1 text-sm text-zinc-500" data-testid="reports-form-selected-range-label">{formatDate(form.start_date)} to {formatDate(form.end_date)}</div>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="reports-form-selected-fields-list">
                  {form.selected_fields.map((fieldKey) => (
                    <span className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-xs font-semibold text-zinc-700" data-testid={`reports-form-selected-field-${fieldKey}`} key={fieldKey}>{REPORT_FIELDS.find((field) => field.key === fieldKey)?.label}</span>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-70" data-testid="reports-generate-button" disabled={pageLoading || generating} onClick={handleGenerate} type="button">
                  {generating ? "Generating..." : "Generate report"}
                </button>
                <button className="inline-flex items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm font-semibold text-zinc-700" data-testid="reports-reset-button" onClick={clearGeneratedReport} type="button">
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {!reportData ? (
        <section className="panel-surface px-5 py-10 text-center" data-testid="reports-empty-state">
          <p className="text-base font-semibold text-zinc-900" data-testid="reports-empty-state-title">No report generated yet.</p>
          <p className="mt-2 text-sm text-zinc-500" data-testid="reports-empty-state-description">Generate one custom report to review it on screen and download the PDF.</p>
        </section>
      ) : (
        <>
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="reports-summary-cards">
            <SummaryCard accent="zinc" helperText={reportData.report_type === "truck-wise" ? "Grouped truck rows in this report." : "Visible rows in the generated report."} label={reportData.report_type === "truck-wise" ? "Tracked trucks" : "Report rows"} testId="reports-summary-record-count" value={reportData.totals.record_count || 0} />
            <SummaryCard accent="blue" helperText="Loads represented in this report." label="Loads" testId="reports-summary-load-count" value={reportData.totals.load_count || 0} />
            <SummaryCard accent="green" helperText={`${reportData.filters.applied_start_date} to ${reportData.filters.applied_end_date}`} label={`Total ${settings.distance_unit === "Kilometers" ? "km" : "miles"}`} testId="reports-summary-distance" value={formatDistance(reportData.totals.distance_miles, settings.distance_unit)} />
            <SummaryCard accent="orange" helperText="Revenue totals remain split by currency." label="Revenue" testId="reports-summary-revenue" value={`${formatMoney(reportData.totals.cad, "CAD")} / ${formatMoney(reportData.totals.usd, "USD")}`} />
          </section>

          <section className="panel-surface space-y-4 px-5 py-5" data-testid="reports-actions-panel">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-zinc-900" data-testid="reports-results-title">{reportData.report_label}</h2>
                <p className="mt-1 text-sm text-zinc-500" data-testid="reports-results-caption">{reportData.description}</p>
              </div>
              <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-70" data-testid="reports-download-pdf-button" disabled={exporting} onClick={handleExportPdf} type="button">
                {exporting ? <Download className="h-4 w-4" /> : <FileText className="h-4 w-4" />} {exporting ? "Downloading PDF..." : "Download PDF"}
              </button>
            </div>
          </section>

          <section className="table-shell" data-testid="reports-results-table-shell">
            <div className="border-b border-zinc-200 px-5 py-4">
              <h3 className="text-xl font-semibold text-zinc-900" data-testid="reports-results-table-title">Report details</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table" data-testid="reports-results-table">
                <thead>
                  <tr>
                    {reportData.display_fields.map((field) => <th data-testid={`reports-results-header-${field.key}`} key={field.key}>{field.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.length ? paginatedRows.map((row) => (
                    <tr data-testid={`reports-results-row-${row.id}`} key={row.id}>
                      {reportData.display_fields.map((field) => (
                        <td data-testid={`reports-results-cell-${row.id}-${field.key}`} key={`${row.id}-${field.key}`}>
                          {formatFieldValue(field.key, row, settings.distance_unit)}
                        </td>
                      ))}
                    </tr>
                  )) : (
                    <tr>
                      <td className="py-10 text-center text-sm text-zinc-500" colSpan={Math.max(reportData.display_fields.length, 1)} data-testid="reports-results-empty">
                        No rows match this report selection.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <TablePagination currentPage={currentPage} pageSize={pageSize} setCurrentPage={setCurrentPage} setPageSize={setPageSize} testIdPrefix="reports-results" totalItems={reportData.rows.length} />
          </section>

          <section className="grid gap-6" data-testid="reports-breakdown-sections">
            {reportData.breakdowns.customer?.length ? <BreakdownTable distanceUnit={settings.distance_unit} rows={reportData.breakdowns.customer} testIdPrefix="reports-breakdown-customer" title="Customer breakdown" /> : null}
            {reportData.breakdowns.driver?.length ? <BreakdownTable distanceUnit={settings.distance_unit} rows={reportData.breakdowns.driver} testIdPrefix="reports-breakdown-driver" title="Driver breakdown" /> : null}
            {reportData.breakdowns.truck?.length ? <BreakdownTable distanceUnit={settings.distance_unit} rows={reportData.breakdowns.truck} testIdPrefix="reports-breakdown-truck" title="Truck breakdown" /> : null}
          </section>
        </>
      )}
    </div>
  );
};
